import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ExternalLink, Download, FileText } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const COPY = {
  de: {
    back: "Zurück",
    overline: "— Rechtliches",
    title: "Allgemeine Geschäftsbedingungen",
    subtitle: "AGB · Fritz Kamm",
    intro:
      "Hier finden Sie unsere aktuellen Allgemeinen Geschäftsbedingungen als PDF-Dokument. Für konkrete Rückfragen zu einzelnen Punkten kontaktieren Sie uns bitte direkt.",
    view_pdf: "AGB als PDF ansehen",
    download_pdf: "AGB herunterladen",
    contact_title: "Rückfragen zu den AGB",
    home: "Zur Startseite",
    rights: "Alle Rechte vorbehalten.",
    updated: "Stand:",
    locale: "de-CH",
  },
  en: {
    back: "Back",
    overline: "— Legal",
    title: "General Terms & Conditions",
    subtitle: "T&C · Fritz Kamm",
    intro:
      "You'll find our current General Terms & Conditions as a PDF document below. For specific questions on individual points, please contact us directly.",
    view_pdf: "View T&C as PDF",
    download_pdf: "Download T&C",
    contact_title: "Questions about the T&C",
    home: "Back to home",
    rights: "All rights reserved.",
    updated: "As of:",
    locale: "en-GB",
  },
  fr: {
    back: "Retour",
    overline: "— Mentions légales",
    title: "Conditions Générales de Vente",
    subtitle: "CGV · Fritz Kamm",
    intro:
      "Vous trouverez ci-dessous nos Conditions Générales de Vente en format PDF. Pour toute question spécifique, veuillez nous contacter directement.",
    view_pdf: "Voir les CGV (PDF)",
    download_pdf: "Télécharger les CGV",
    contact_title: "Questions sur les CGV",
    home: "Retour à l'accueil",
    rights: "Tous droits réservés.",
    updated: "État :",
    locale: "fr-CH",
  },
  it: {
    back: "Indietro",
    overline: "— Note legali",
    title: "Condizioni Generali di Vendita",
    subtitle: "CGV · Fritz Kamm",
    intro:
      "Qui trova le nostre Condizioni Generali di Vendita in formato PDF. Per domande specifiche La preghiamo di contattarci direttamente.",
    view_pdf: "Vedi CGV (PDF)",
    download_pdf: "Scarica CGV",
    contact_title: "Domande sulle CGV",
    home: "Torna alla home",
    rights: "Tutti i diritti riservati.",
    updated: "Aggiornato al:",
    locale: "it-CH",
  },
};

const AGB_PDF_URL = "/downloads/Swissgalvanic_AGB.pdf";

export default function AGB() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="agb-back-home" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 border border-white/30 flex items-center justify-center font-display font-semibold text-[13px] tracking-tighter">
              FK
            </div>
            <span className="font-display text-[15px] font-semibold tracking-tight">
              Fritz Kamm
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-zinc-400 hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" />
            {c.back}
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="overline mb-5">{c.overline}</div>
        <h1 data-testid="agb-title" className="font-display text-4xl md:text-6xl font-semibold tracking-tight leading-[0.95] mb-4">
          {c.title}
        </h1>
        <div className="text-zinc-500 text-sm mb-10">{c.subtitle}</div>

        <p className="text-zinc-300 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
          {c.intro}
        </p>

        {/* PDF actions */}
        <div className="flex flex-wrap gap-3 mb-10">
          <a
            href={AGB_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="agb-view-pdf"
            className="group inline-flex items-center gap-2 bg-white text-black px-5 py-3 text-sm font-semibold hover:bg-zinc-200 transition-colors"
          >
            <FileText className="w-4 h-4" />
            {c.view_pdf}
            <ExternalLink className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={AGB_PDF_URL}
            download="Fritz-Kamm-AGB.pdf"
            data-testid="agb-download-pdf"
            className="group inline-flex items-center gap-2 border border-white/20 hover:border-white text-white text-sm font-medium px-5 py-3 hover:bg-white hover:text-black transition-colors"
          >
            <Download className="w-4 h-4" />
            {c.download_pdf}
          </a>
        </div>

        {/* Embedded PDF preview — hidden on very small screens where it won't render usefully */}
        <div className="hidden md:block mb-12 border border-white/10 bg-black">
          <object
            data={AGB_PDF_URL}
            type="application/pdf"
            className="w-full h-[820px]"
            aria-label="AGB PDF"
          >
            <div className="p-8 text-zinc-400 text-sm">
              PDF-Vorschau nicht verfügbar.{" "}
              <a href={AGB_PDF_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
                {c.view_pdf}
              </a>
            </div>
          </object>
        </div>

        <div className="border border-white/10 p-6 md:p-8 bg-[#121214] max-w-2xl">
          <div className="overline mb-3 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" />
            {c.contact_title}
          </div>
          <a
            href="mailto:sekretariat@f-kamm.ch"
            data-testid="agb-contact-mail"
            className="font-display text-lg md:text-xl text-white hover:text-zinc-300 transition-colors"
          >
            sekretariat@f-kamm.ch
          </a>
          <div className="mt-2 text-zinc-400 text-sm">
            <a href="tel:+41817231642" className="hover:text-white transition-colors">
              +41 81 723 16 42
            </a>
          </div>
        </div>

        <div className="mt-10 text-[12px] text-zinc-500">
          {c.updated} {new Date().toLocaleDateString(c.locale, { month: "long", year: "numeric" })}
        </div>
      </main>

      <footer className="border-t border-white/10 mt-12 py-8">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-[12px] text-zinc-500 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <span>© {new Date().getFullYear()} Fritz Kamm · {c.rights}</span>
          <Link to="/" className="hover:text-white transition-colors">{c.home}</Link>
        </div>
      </footer>
    </div>
  );
}
